// src/contexts/AppStateContext.jsx
import React, { createContext, useContext, useState, useMemo, useCallback, useEffect } from '@wordpress/element';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import * as storage from '../browserStorage';
import { storageItemName } from '../config';
import { getExistingCheckout, consoleDebug } from '../util';
import { useConfig } from './ConfigContext';

const AppStateContext = createContext(null);

export const useAppState = () => {
    const context = useContext(AppStateContext);
    if (!context) {
        throw new Error('useAppState must be used within AppStateProvider');
    }
    return context;
};

export const AppStateProvider = ({ children }) => {
    const params = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    
    const { 
        locationGateConfig, 
        isSingleRetailer, 
        singleRetailerId,
        retailers,
        nonHeadlessRetailers,
        getRetailerById,
        getRetailerBySlug,
        shortcodeData,
        allRetailersConfig,
    } = useConfig();
    
    // Core app state
    const [isInitialized, setIsInitialized] = useState(false);
    const [fallbackEnabled, setFallbackEnabled] = useState(false);
    
    // Retailer state
    const [retailerId, setRetailerId] = useState(null);
    const [nonHeadlessRetailer, setNonHeadlessRetailer] = useState(
        storage.getItem('NON_HEADLESS_RETAILER')
    );
    const [nonHeadlessRetailerData, setNonHeadlessRetailerData] = useState(null);
    
    // UI state
    const [sidebarToggle, setSidebarToggle] = useState(false);
    const [productPopupData, setProductPopupData] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    
    // Gate state
    const [ageGatePassed, setAgeGatePassed] = useState(() => 
        storage.getItem('HEADLESS_AGE_GATE_PASSED') === 'true'
    );
    const [locationSelected, setLocationSelected] = useState(false);
    const [locationRedirect, setLocationRedirect] = useState(false);
    
    // Cart rendering state
    const [shouldRenderCart, setShouldRenderCart] = useState(false);
    
    // Initialize retailer on mount
    // Initialize retailer on mount
useEffect(() => {
    if (isInitialized || !retailers || Object.keys(retailers).length === 0) return;
    
    consoleDebug('🔍 AppStateContext: Initializing retailer selection', {
        hasRetailers: !!retailers,
        retailerCount: Object.keys(retailers).length,
        isSingleRetailer,
        params,
        shortcodeData
    });
    
    let newRetailerId = null;
    let foundRetailer = false;
    
    // Priority 1: From shortcode
    const retailerShortcode = Object.values(shortcodeData).find(
        data => data.shortcode === 'headlessproducts' && data?.retailerShortcode?.providedId
    )?.retailerShortcode?.providedId;
    
    if (retailerShortcode) {
        newRetailerId = retailerShortcode;
        foundRetailer = true;
        consoleDebug('✅ Found retailer from shortcode:', retailerShortcode);
    }
    // Priority 2: Check existing checkout BEFORE URL check
    // This ensures we always check localStorage first
    else {
        const checkout = getExistingCheckout();
        if (checkout?.retailerId && retailers[checkout.retailerId]) {
            newRetailerId = checkout.retailerId;
            foundRetailer = true;
            consoleDebug('✅ Found retailer from existing checkout:', checkout.retailerId);
        }
        // Priority 3: From URL (only if no checkout exists)
        else if (params?.locationSlug) {
            // Check if locationSlug is actually a retailer slug
            const retailerMatch = getRetailerBySlug(params.locationSlug);
            if (retailerMatch) {
                newRetailerId = retailerMatch.id;
                foundRetailer = true;
                consoleDebug('✅ Found retailer from URL slug:', params.locationSlug);
            }
            // If not, check if we have a stored retailer and this is a filter URL
            else {
                const categoryTypes = ['brand', 'category', 'tags', 'effect', 'specials', 'subcategory'];
                if (categoryTypes.includes(params.locationSlug)) {
                    // Check existing checkout again for filter URLs
                    const filterCheckout = getExistingCheckout();
                    if (filterCheckout?.retailerId && retailers[filterCheckout.retailerId]) {
                        newRetailerId = filterCheckout.retailerId;
                        foundRetailer = true;
                        consoleDebug('✅ Found retailer from checkout for filter URL:', filterCheckout.retailerId);
                    }
                    // If still no retailer and it's a single retailer site, use the single retailer
                    else if (isSingleRetailer && singleRetailerId) {
                        newRetailerId = singleRetailerId;
                        foundRetailer = true;
                        consoleDebug('✅ Using single retailer for filter URL:', singleRetailerId);
                    }
                }
            }
        }
    }
    
    // Priority 4: Single retailer
    if (!newRetailerId && isSingleRetailer) {
        newRetailerId = singleRetailerId;
        foundRetailer = true;
        consoleDebug('✅ Using single retailer:', singleRetailerId);
    }
    
    // If we found a retailer, set it
    if (newRetailerId) {
        setRetailerId(newRetailerId);
        setLocationSelected(true);
        consoleDebug('✅ Retailer set:', newRetailerId);
    } else {
        // Before showing location gate, double-check localStorage one more time
        // This handles edge cases where the checkout might have been set after initial load
        const finalCheckout = getExistingCheckout();
        if (finalCheckout?.retailerId && retailers[finalCheckout.retailerId]) {
            setRetailerId(finalCheckout.retailerId);
            setLocationSelected(true);
            consoleDebug('✅ Found retailer from final checkout check:', finalCheckout.retailerId);
        } else {
            // No retailer found - this is important for multi-retailer sites
            consoleDebug('⚠️ No retailer found - will show location gate');
            // Explicitly set locationSelected to false to ensure location gate shows
            setLocationSelected(false);
        }
    }
    
    // Check for non-headless retailer
    const currentPath = window.location.pathname;
    if (/^\/location\//.test(currentPath) && !retailerShortcode) {
        const matchingRetailer = Object.values(nonHeadlessRetailers).find(retailer =>
            window.location.href.startsWith(retailer.non_headless_location_homepage)
        );
        
        if (matchingRetailer) {
            setNonHeadlessRetailer(matchingRetailer.location_name);
            setNonHeadlessRetailerData(matchingRetailer);
            consoleDebug('✅ Found non-headless retailer:', matchingRetailer.location_name);
        }
    }
    
    setIsInitialized(true);
    consoleDebug('✅ AppStateContext initialization complete', {
        retailerId: newRetailerId,
        locationSelected: foundRetailer,
        nonHeadlessRetailer: !!nonHeadlessRetailer
    });
}, [retailers, params?.locationSlug, shortcodeData, isSingleRetailer, singleRetailerId, nonHeadlessRetailers, getRetailerBySlug]);
    
    // Handle navigation
    useEffect(() => {
        if (!isInitialized || !retailerId || !retailers[retailerId]) return;
        
        const menuSlug = retailers[retailerId]?.menuSlug;
        if (!menuSlug) return;
        
        // Preserve query params
        const searchParams = new URLSearchParams(location.search);
        const preservedParams = new URLSearchParams();
        ['search', 'thcMin', 'thcMax', 'menuType'].forEach(key => {
            if (searchParams.has(key)) {
                preservedParams.set(key, searchParams.get(key));
            }
        });
        const queryString = preservedParams.toString();
        const queryStringWithQuestionMark = queryString ? `?${queryString}` : '';
        
        // Single retailer redirect
        if (isSingleRetailer && params?.viewName === 'shop' && !params?.locationSlug) {
            navigate(`/shop/${menuSlug}/${queryStringWithQuestionMark}`, { replace: true });
            return;
        }
        
        // Handle direct category navigation WITHOUT a retailer slug
        const categoryTypes = ['brand', 'category', 'tags', 'effect', 'specials', 'subcategory'];
        if (params?.viewName === 'shop' && categoryTypes.includes(params?.locationSlug) && !params?.categoryType) {
            // This means we have a URL like /shop/category/flower where 'category' is in locationSlug position
            // We need to redirect to /shop/{menuSlug}/category/{categoryName}
            const categoryType = params.locationSlug;
            const categoryName = params.categoryType || ''; // In this misaligned case, categoryType param contains the category name
            
            let redirectPath = `/shop/${menuSlug}/${categoryType}/`;
            if (categoryName) {
                redirectPath += `${categoryName}/`;
            }
            redirectPath += queryStringWithQuestionMark;
            
            navigate(redirectPath, { 
                state: { setFilters: true }, // Allow filters to be set from the URL
                replace: true 
            });
            return;
        }
        
        // Also handle the case where we have /shop/category/flower/something
        // In this case: viewName='shop', locationSlug='category', categoryType='flower', categoryName='something'
        if (params?.viewName === 'shop' && categoryTypes.includes(params?.locationSlug) && params?.categoryType) {
            // Check if the locationSlug is NOT a valid retailer slug
            const retailerMatch = getRetailerBySlug(params.locationSlug);
            if (!retailerMatch) {
                const categoryType = params.locationSlug;
                const categoryName = params.categoryType;
                const additionalParam = params.categoryName || '';
                
                let redirectPath = `/shop/${menuSlug}/${categoryType}/${categoryName}/`;
                if (additionalParam) {
                    redirectPath += `${additionalParam}/`;
                }
                redirectPath += queryStringWithQuestionMark;
                
                navigate(redirectPath, { 
                    state: { setFilters: true },
                    replace: true 
                });
                return;
            }
        }
        
        // Handle base /shop/ redirects
        if (params?.viewName === 'shop' && (!params?.locationSlug || params.locationSlug === 'undefined')) {
            navigate(`/shop/${menuSlug}/${queryStringWithQuestionMark}`, { 
                state: { setFilters: false },
                replace: true 
            });
        }
    }, [isInitialized, retailerId, retailers, params, location.search, navigate, isSingleRetailer, getRetailerBySlug]);
    
    // Determine cart rendering
    useEffect(() => {
        const hasValidShortcode = Object.values(shortcodeData).some(
            data => ['singleproduct', 'headlessproducts', 'headlessmenu', 'shopnavfull'].includes(data.shortcode)
        );
        setShouldRenderCart(!nonHeadlessRetailer && hasValidShortcode);
    }, [shortcodeData, nonHeadlessRetailer]);
    
    // Update fallback enabled when retailer changes
    useEffect(() => {
        if (retailerId && window.rangeHeadlessApi?.fallback?.[retailerId]) {
            setFallbackEnabled(window.rangeHeadlessApi.fallback[retailerId].enabled || false);
        }
    }, [retailerId]);
    
    // Computed values
    const currentRetailer = useMemo(() => 
        retailerId ? getRetailerById(retailerId) : null,
        [retailerId, getRetailerById]
    );
    
    const needsAgeGate = useMemo(() => {
        const needs = locationGateConfig?.enableAgeGate && !ageGatePassed && !nonHeadlessRetailer;
        consoleDebug('🔍 needsAgeGate check:', {
            enableAgeGate: locationGateConfig?.enableAgeGate,
            ageGatePassed,
            nonHeadlessRetailer: !!nonHeadlessRetailer,
            result: needs
        });
        return needs;
    }, [locationGateConfig, ageGatePassed, nonHeadlessRetailer]);
    
    const needsLocationGate = useMemo(() => {
        const needs = !isSingleRetailer && !locationSelected && !retailerId && !nonHeadlessRetailer;
        consoleDebug('🔍 needsLocationGate check:', {
            isSingleRetailer,
            locationSelected,
            retailerId,
            nonHeadlessRetailer: !!nonHeadlessRetailer,
            result: needs
        });
        return needs;
    }, [isSingleRetailer, locationSelected, retailerId, nonHeadlessRetailer]);
    
    const gatesPassed = useMemo(() => {
        const passed = (!needsLocationGate || locationSelected) && 
                      (!needsAgeGate || ageGatePassed);
        consoleDebug('🔍 gatesPassed check:', {
            needsLocationGate,
            locationSelected,
            needsAgeGate,
            ageGatePassed,
            result: passed
        });
        return passed;
    }, [needsLocationGate, locationSelected, needsAgeGate, ageGatePassed]);
    
    const isAppReady = useMemo(() => {
        const ready = isInitialized && gatesPassed;
        consoleDebug('🔍 isAppReady check:', {
            isInitialized,
            gatesPassed,
            result: ready
        });
        return ready;
    }, [isInitialized, gatesPassed]);
    
    // Retailer management with navigation
    const updateRetailerId = useCallback((newRetailerId) => {
        consoleDebug('🔄 updateRetailerId called:', {
            current: retailerId,
            new: newRetailerId
        });
        
        if (newRetailerId === retailerId) return;
        
        setRetailerId(newRetailerId);
        setLocationSelected(true);
        
        // Clear cart if retailer changed
        const checkout = getExistingCheckout();
        if (checkout && checkout.retailerId !== newRetailerId) {
            storage.removeItem(storageItemName);
            consoleDebug('🗑️ Cleared cart for retailer change');
        }
        
        // Handle redirects
        const retailer = retailers[newRetailerId];
        if (retailer && allRetailersConfig) {
            setTimeout(() => {
                if (allRetailersConfig.locationMenuRedirect && retailer.menuSlug) {
                    if (!window.location.pathname.startsWith(`/shop/${retailer.menuSlug}`)) {
                        consoleDebug('🔄 Redirecting to menu:', `/shop/${retailer.menuSlug}/`);
                        window.location.href = `/shop/${retailer.menuSlug}/`;
                    }
                } else if (allRetailersConfig.locationHomepageRedirect && retailer.locationHomepage) {
                    consoleDebug('🔄 Redirecting to homepage:', retailer.locationHomepage);
                    window.location.href = retailer.locationHomepage;
                }
            }, 300);
        }
    }, [retailerId, retailers, allRetailersConfig]);
    
    // UI functions
    const openSidebar = useCallback(() => setSidebarToggle(true), []);
    const closeSidebar = useCallback(() => setSidebarToggle(false), []);
    const toggleSidebar = useCallback(() => setSidebarToggle(prev => !prev), []);
    
    const openProductPopup = useCallback((productData) => {
        setProductPopupData(productData);
    }, []);
    
    const closeProductPopup = useCallback(() => {
        setProductPopupData(null);
    }, []);
    
    // Gate functions
    const passAgeGate = useCallback(() => {
        storage.setItem('HEADLESS_AGE_GATE_PASSED', 'true');
        setAgeGatePassed(true);
        consoleDebug('✅ Age gate passed');
    }, []);
    
    const resetGates = useCallback(() => {
        storage.removeItem('HEADLESS_AGE_GATE_PASSED');
        setAgeGatePassed(false);
        setLocationSelected(false);
        consoleDebug('🔄 Gates reset');
    }, []);
    
    const contextValue = useMemo(() => ({
        // Core app state
        isInitialized,
        isAppReady,
        fallbackEnabled,
        
        // Retailer state
        retailerId,
        setRetailerId: updateRetailerId,
        currentRetailer,
        nonHeadlessRetailer,
        setNonHeadlessRetailer,
        nonHeadlessRetailerData,
        setNonHeadlessRetailerData,
        
        // UI state
        sidebarToggle,
        setSidebarToggle,
        openSidebar,
        closeSidebar,
        toggleSidebar,
        productPopupData,
        setProductPopupData,
        openProductPopup,
        closeProductPopup,
        mobileMenuOpen,
        setMobileMenuOpen,
        shouldRenderCart,
        
        // Gate state
        ageGatePassed,
        locationSelected,
        locationRedirect,
        setLocationRedirect,
        needsAgeGate,
        needsLocationGate,
        gatesPassed,
        passAgeGate,
        resetGates,
    }), [
        isInitialized,
        isAppReady,
        fallbackEnabled,
        retailerId,
        updateRetailerId,
        currentRetailer,
        nonHeadlessRetailer,
        nonHeadlessRetailerData,
        sidebarToggle,
        openSidebar,
        closeSidebar,
        toggleSidebar,
        productPopupData,
        openProductPopup,
        closeProductPopup,
        mobileMenuOpen,
        shouldRenderCart,
        ageGatePassed,
        locationSelected,
        locationRedirect,
        needsAgeGate,
        needsLocationGate,
        gatesPassed,
        passAgeGate,
        resetGates,
    ]);

    return (
        <AppStateContext.Provider value={contextValue}>
            {children}
        </AppStateContext.Provider>
    );
};
